import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { GRADES, COMPANY } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  grade: "",
  quantity: "",
  location: "",
  required_date: "",
  message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.grade || !form.quantity || !form.location) {
      toast.error("Please fill name, phone, grade, quantity and location.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Enquiry received. Our team will call you shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/25 py-4 text-lg text-[#F5F4EF] placeholder:text-[#F5F4EF]/40 focus:border-primary focus:outline-none transition-colors";

  return (
    <section data-testid="quote" id="quote" className="dark bg-black text-[#F5F4EF] py-28 md:py-40 grain relative">
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="font-mono-accent text-xs uppercase tracking-[0.25em] text-signal-yellow">
            /04 — Request a quote
          </span>
          <h2 className="font-display mt-6 text-4xl md:text-6xl tracking-tighter leading-[0.95]">
            Tell us your pour. We&apos;ll load the trucks.
          </h2>
          <p className="mt-8 text-muted-foreground leading-relaxed max-w-md">
            Share your grade, volume and site. Our team responds fast — day or night — with pricing and the earliest delivery slot.
          </p>
          <div className="mt-10 border border-white/15 p-6">
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F5F4EF]/50 mb-2">
              Prefer to talk?
            </p>
            <a href={`tel:${COMPANY.phoneRaw}`} className="font-display text-3xl text-primary hover:text-signal-yellow transition-colors">
              {COMPANY.phone}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div data-testid="quote-success" className="border border-primary p-12 flex flex-col items-start gap-6">
              <CheckCircle2 size={56} className="text-primary" />
              <h3 className="font-display text-3xl tracking-tight">Enquiry received.</h3>
              <p className="text-muted-foreground max-w-md">
                Thank you. Our team will reach out on the number you provided. For urgent pours, call us directly — we&apos;re open 24×7.
              </p>
              <button
                data-testid="quote-reset-btn"
                onClick={() => setDone(false)}
                className="font-mono-accent text-xs uppercase tracking-[0.2em] border border-white/25 px-6 py-4 hover:border-primary hover:text-primary transition-colors"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form data-testid="quote-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <input data-testid="quote-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
              <input data-testid="quote-phone" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
              <input data-testid="quote-email" className={inputCls} placeholder="Email (optional)" value={form.email} onChange={set("email")} />
              <select data-testid="quote-grade" className={`${inputCls} appearance-none`} value={form.grade} onChange={set("grade")}>
                <option value="" className="bg-black">Select grade *</option>
                {GRADES.map((g) => (
                  <option key={g.code} value={g.code} className="bg-black">{g.code} — {g.strength}</option>
                ))}
              </select>
              <input data-testid="quote-quantity" className={inputCls} placeholder="Quantity (e.g. 50 m³) *" value={form.quantity} onChange={set("quantity")} />
              <input data-testid="quote-location" className={inputCls} placeholder="Delivery location *" value={form.location} onChange={set("location")} />
              <input data-testid="quote-date" type="date" className={`${inputCls} md:col-span-2`} value={form.required_date} onChange={set("required_date")} />
              <textarea data-testid="quote-message" rows={3} className={`${inputCls} md:col-span-2 resize-none`} placeholder="Additional details (site access, pump required, etc.)" value={form.message} onChange={set("message")} />
              <button
                data-testid="quote-submit-btn"
                type="submit"
                disabled={loading}
                className="md:col-span-2 flex items-center justify-center gap-3 bg-primary text-white py-6 font-mono-accent text-xs uppercase tracking-[0.25em] hover:bg-[#F5F4EF] hover:text-black transition-colors disabled:opacity-60"
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Send Enquiry"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
